import React from "react";
import { Box, CircularProgress } from "@mui/material";
import { AssignmentOutlined } from "@mui/icons-material";
import { SrCitation } from "../models/chatBotModels";
import { dt } from "../../themes/redesignTokens";

// ═══════════════════════════════════════════════════════════════
//  ChatSrCitationChip (MC-22)
//  Gold-tinted pill chip rendering "SR-YYMMDD-NNN" with optional
//  "×N" count badge. Three visual states: default / viewed / active.
//  Click behavior is delegated to the parent (onClick); the parent
//  decides whether to open the MIDAS artifact pane or navigate.
// ═══════════════════════════════════════════════════════════════

// Local MIDAS gold palette (matches ChatBotMessage.midas)
const GOLD = "#D4A017";
const GOLD_SOFT_BG = "rgba(212, 160, 23, 0.06)";
const GOLD_VIEWED_BG = "rgba(212, 160, 23, 0.15)";
const GOLD_BORDER = "rgba(212, 160, 23, 0.35)";
const GOLD_BORDER_STRONG = "rgba(212, 160, 23, 0.55)";

export interface ChatSrCitationChipProps {
  citation: SrCitation;
  /** True when this SR is currently showing in the active artifact tab */
  isActive?: boolean;
  /** True when this chip was clicked at least once this session */
  isViewed?: boolean;
  /** True while the click handler is async-resolving machineId */
  isResolving?: boolean;
  /** Kiosk mode — chip renders but ignores click */
  isKiosk?: boolean;
  /** Click handler. Parent decides behavior (artifact pane vs navigate). */
  onClick?: (citation: SrCitation) => void;
}

const ChatSrCitationChip: React.FC<ChatSrCitationChipProps> = ({
  citation,
  isActive = false,
  isViewed = false,
  isResolving = false,
  isKiosk = false,
  onClick,
}) => {
  const clickable = !isKiosk && !isResolving && !!onClick;

  // Visual state selection
  let bg = GOLD_SOFT_BG;
  let border = GOLD_BORDER;
  let labelColor = dt.textPrimary;
  let iconColor = GOLD;
  if (isActive) {
    bg = GOLD;
    border = GOLD;
    labelColor = "#FFFFFF";
    iconColor = "#FFFFFF";
  } else if (isViewed) {
    bg = GOLD_VIEWED_BG;
  }

  const handleClick = () => {
    if (!clickable) return;
    onClick?.(citation);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!clickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.(citation);
    }
  };

  return (
    <Box
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : -1}
      aria-label={`Service report ${citation.reportNumber}${
        citation.citationCount > 1 ? ` (${citation.citationCount} citations)` : ""
      }`}
      aria-disabled={!clickable}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        height: "26px",
        padding: "0 10px",
        borderRadius: "999px",
        backgroundColor: bg,
        border: `1px solid ${border}`,
        cursor: clickable ? "pointer" : "default",
        transition: "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
        userSelect: "none",
        opacity: isResolving ? 0.7 : 1,
        "&:hover": clickable && !isActive
          ? {
              borderColor: GOLD_BORDER_STRONG,
              transform: "translateY(-1px)",
              boxShadow: "0 2px 6px rgba(212, 160, 23, 0.2)",
            }
          : undefined,
        "&:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 2px rgba(212, 160, 23, 0.35)`,
        },
      }}
    >
      {isResolving ? (
        <CircularProgress size={11} thickness={5} sx={{ color: iconColor }} />
      ) : (
        <AssignmentOutlined sx={{ fontSize: "13px", color: iconColor }} />
      )}
      <Box
        sx={{
          fontFamily: dt.fontMono,
          fontSize: "11px",
          fontWeight: 600,
          color: labelColor,
          letterSpacing: "0.02em",
          lineHeight: 1,
        }}
      >
        {citation.reportNumber}
      </Box>
      {citation.citationCount > 1 && (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "16px",
            height: "14px",
            padding: "0 4px",
            borderRadius: "999px",
            backgroundColor: isActive ? "#FFFFFF" : GOLD,
            color: isActive ? GOLD : "#FFFFFF",
            fontFamily: dt.fontMono,
            fontSize: "9px",
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {`\u00D7${citation.citationCount}`}
        </Box>
      )}
    </Box>
  );
};

export default ChatSrCitationChip;
